import {
  AccessTime,
  Box,
  EditOutlinedIcon,
  Stack,
  Typography,
  VisibilityOutlinedIcon,
} from '@cmg/design-system';
import React from 'react';

import type { ConsentScope } from '../../../../types/domain/consent/consentInfo';
import type { ScopeKind } from '../ConsentScreen.model';
import { scopeKind } from '../ConsentScreen.model';
import { onDark } from '../consentSurface';

const scopeIcons: Record<ScopeKind, typeof VisibilityOutlinedIcon> = {
  read: VisibilityOutlinedIcon,
  write: EditOutlinedIcon,
  persistent: AccessTime,
};

/**
 * One row per scope. The icon states the kind rather than repeating a tick down the column: a
 * checkmark means *granted* everywhere else in software, so a column of them read as "all set" on
 * the one screen whose job is to stay neutral about the answer.
 *
 * Every row is typeset identically. Writing to a live order book and reading an email address are
 * told apart by the glyph alone, which is a weaker signal than emphasis — but a list where some
 * descriptions are brighter than others reads as a rendering inconsistency before it reads as a
 * warning.
 */
export const Scope: React.FC<{ scope: ConsentScope }> = ({ scope }) => {
  const Glyph = scopeIcons[scopeKind(scope)];

  return (
    <Stack component="li" direction="row" gap={1} alignItems="flex-start">
      {/* Sized and nudged to sit on the cap height of the `body1` name beside it, not on its line
          box. */}
      <Glyph sx={{ ...onDark.mutedText, fontSize: 18, flexShrink: 0, mt: '1px' }} />
      <Box>
        <Typography variant="h3" sx={{ textTransform: 'none' }}>
          {scope.displayName ?? scope.name}
        </Typography>
        {scope.description && (
          <Typography variant="body1" sx={{ ...onDark.mutedText, mt: 0.5 }}>
            {scope.description}
          </Typography>
        )}
      </Box>
    </Stack>
  );
};

export default Scope;
